import Vue from 'vue'

// Documentation for autocomplete at https://www.npmjs.com/package/vuejs-auto-complete
import autocomplete from 'vuejs-auto-complete'
import { isValidEmailFormat, emailExists, EmailLookupState } from "@/utils/email-lookup";

const locationState = Object.freeze({ valid: 1, pending: 0, invalid: -1 });
const planType = Object.freeze({ monthly: 'Monthly', yearly: 'Yearly' });
Vue.config.productionTip = false;

// locationSequence will be used to generate a local id, for the location object
// the location id will be used by vue v-for as key, to allow the user to delete locations
let locationSequence = 0;

function Location() {
    this.key = '';
    this.name = '';
    this.state = locationState.pending;
    this.error = '';
    this.id = ++locationSequence; // a unique identifier

    const eventTypes = window?.createAccountConfig?.eventTypes || [];

    this.eventTypes = JSON.parse(JSON.stringify(eventTypes));
}

new Vue({
    el: '#create-account-component',
    components: {
        autocomplete
    },
    data() {
        return {
            EmailLookupState: EmailLookupState,
            email: '',
            password: '',
            industry: '',
            firstName: '',
            lastName: '',
            hidePassword: true,
            emailLookupState: EmailLookupState.Pending,
            confirmUnload: false,
            locationState,
            planType,
            numbers: ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'],
            locations: window.createAccountConfig.plan.id == 1 ? [new Location()] : [new Location(), new Location(), new Location()],
            maximumLocationsAllowed: window?.createAccountConfig?.maximumLocationsAllowed || 1,
            eventTypes: window?.createAccountConfig?.eventTypes || [],
            plan: window.createAccountConfig.plan,
            industries: window.createAccountConfig.industries,
            isTermsOfUseAccepted: false,
            changePlanUrl: window.createAccountConfig.changePlanUrl || "/#plans",
            referredFrom: 999,
            referredFromLst: window.createAccountConfig.referredFromLst,
            isEventTypesVisible: false,
            addOnPrice: window.createAccountConfig.addOnPrice,
            totalAddOn: 0,
            symbol: '$',
            totalOrderSummary: 0,
            isManageLocationsAddOnEnabled: window.createAccountConfig.isManageLocationsAddOnEnabled == 'True',
            allowMarketingEmails: true
        }
    },
    created() {

    },
    computed: {
        passwordInputType() {
            return this.hidePassword ? 'password' : 'text';
        },
        passwordLinkText() {
            return this.hidePassword ? 'Show' : 'Hide';
        },
        canAddLocation() {
            return this.locations.every(location => location.state === locationState.valid);
        },
        isPasswordValid() {
            if (this.password) {
                const passwordPattern = /(?=.{8,64})((?=.*\d)(?=.*[a-z])(?=.*[A-Z])|(?=.*\d)(?=.*[!@#$%^&*()\-_=+{};:,<.>])(?=.*[a-zA-Z])|(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()\-_=+{};:,<.>])).*$/;
                return passwordPattern.test(this.password);
            }

            return true;
        },
        isPasswordEntered() {
            if (this.password && this.password.length > 0) {
                return true;
            }
            return false;
        },
        isEmailEntered() {
            if (this.email && this.email.length > 0) {
                return true;
            }
            return false;
        },
        isFirstNameEntered() {
            if (this.firstName && this.firstName.length > 0) {
                return true;
            }
            return false;
        },
        isLastNameEntered() {
            if (this.lastName && this.lastName.length > 0) {
                return true;
            }
            return false;
        },
        isIndustryEntered() {
            if (this.industry != '') {
                return true;
            }
            return false;
        },
        isFormRow1Valid() {
            if (!this.isPasswordValid || this.emailLookupState === EmailLookupState.Exists) {
                return false;
            }
            return true;
        },
        isRequiredFieldsEntered() {
            if (!this.isEmailEntered || !this.isPasswordEntered || !this.isFirstNameEntered || !this.isLastNameEntered || !this.isIndustryEntered) {
                return false;
            }
            return true;
        },
        isFormValid() {
            const isValid = this.emailLookupState !== EmailLookupState.Exists
                && this.locations.some(location => location.state === locationState.valid)
                && !this.locations.some(location => location.state === locationState.invalid)
                && this.isTermsOfUseAccepted
                && this.isPasswordValid

            if (isValid) {
                // Validate event types
                const validLocations = this.locations.filter(x => x.state === locationState.valid);
                return validLocations.every(location => location.eventTypes.some(e => e.isChecked));
            }

            return isValid;
        },
        numberOfAddOns() {
            return this.locations.filter(l => l.state == locationState.valid).length - this.maximumLocationsAllowed;
        }
    },
    watch: {
        email: function (value) {
            this.resetEmailStatus();

            if (isValidEmailFormat(value)) {
                this.lookupEmail(value).then(status => {
                    this.emailLookupState = status;
                });
            }
        }
    },
    mounted() {
        this.removeNativeAutocomplete();
    },
    methods: {
        resetEmailStatus: function () {
            this.emailLookupState = EmailLookupState.Pending;
        },
        lookupEmail: function (emailAddress) {
            return emailExists(emailAddress);
        },
        checkForm: function (e) {

            if (this.emailLookupState === EmailLookupState.NotExists) {
                return true;
            }

            e.preventDefault();
            return false;
        },
        addLocation: function (e) {
            e.preventDefault();

            if (this.canAddLocation) {
                this.locations.push(new Location());
                this.removeNativeAutocomplete();
                this.orderSummary();
            }
        },
        setLocation(index, selection) {
            let item = this.locations[index];
            item.name = selection.display;
            item.key = selection.value;
            item.error = '';
            item.state = locationState.valid;

            const isDuplicated = this.locations.filter(x => x.key === item.key).length > 1;
            if (isDuplicated) {
                this.setLocationError(index, `${item.name} already selected. Please enter unique locations.`);
                return;
            }
            this.orderSummary();
        },
        setLocationError(index, message) {
            let item = this.locations[index];
            item.state = locationState.invalid;
            item.error = message;
        },
        clearLocation(index) {
            const location = this.locations[index];

            location.name = '';
            location.key = '';
            location.state = locationState.pending;
            location.error = '';
            location.eventTypes.forEach(e => e.isChecked = true);
            this.orderSummary();
        },
        onNothingSelected(index, text) {
            this.setLocationError(index, text + ' is not a valid location');
        },
        removeNativeAutocomplete() {
            setTimeout(() => {
                const autocompleteInputs = document.getElementsByClassName('autocomplete');
                Array.from(autocompleteInputs).forEach(input => input.setAttribute('autocomplete', 'nope'));
            }, 400);
        },
        onNoResults(index) {
            this.setLocationError(index, 'Invalid location, please try typing the city again and select one from the list');
        },
        removeLocation(index, e) {
            e.preventDefault();
            this.locations.splice(index, 1);
            this.orderSummary();
        },
        getLocationResults: function (input) {
            let focusedElement;
            if (this.$refs.autocomplete0[0].isFocussed === true) {
                focusedElement = this.$refs.autocomplete0[0];
            }
            else if (this.$refs.autocomplete1[0].isFocussed === true) {
                focusedElement = this.$refs.autocomplete1[0];
            }
            else if (this.$refs.autocomplete2[0].isFocussed === true) {
                focusedElement = this.$refs.autocomplete2[0];
            }
            else if (this.$refs.autocomplete3[0].isFocussed === true) {
                focusedElement = this.$refs.autocomplete3[0];
            }
            else {
                focusedElement = this.$refs.autocomplete4[0];
            }
            var currentSearchInput = document.getElementsByName(focusedElement.$el.id)[0].previousElementSibling.value ?? "";
            if (currentSearchInput.length >= 3) {
                return '/api/location/autocomplete/?searchTerm=' + input
            }
            return '/api/location/check'
        },
        createUser: function () {
            if (!this.isRequiredFieldsEntered) {
                if (!this.isEmailEntered || !this.isPasswordEntered) {
                    document.getElementById('required-msg-1').style.display = "block";
                }
                if (!this.isFirstNameEntered || !this.isLastNameEntered) {
                    document.getElementById('required-msg-2').style.display = "block";
                }
                if (!this.isIndustryEntered) {
                    document.getElementById('required-msg-3').style.display = "block";
                }
                return
            }

            this.setHubspotIdentity();

            document.getElementById("createForm").submit();
        },
        setHubspotIdentity: function () {
            const data = {
                email: this.email,
                plan: this.plan.name,
                first_name: this.firstName,
                last_name: this.lastName,
                marketing_updates: this.allowMarketingEmails,
                industry: document.querySelector('#industry option:checked').innerHTML
            };

            for (var i = 0; i < this.locations.length; i++) {
                data[`location_${i + 1}`] = this.locations[i].name;
            }

            var _hsq = window._hsq = window._hsq || [];
            _hsq.push(["identify", data]);
        },
        orderSummary: function () {
            this.totalAddOn = this.numberOfAddOns * this.addOnPrice;
            this.totalOrderSummary = this.totalAddOn + parseInt(this.plan.price.replace('$', ''));
        }
    },
    template: `
<form id="createForm" method="post" autocomplete="false" class="create-account" v-on:submit.prevent="createUser()">
    <div class="create-account__form-fields">
        <input type="hidden" name="TierId" :value="plan.id" />
        <input type="hidden" name="planType" :value="plan.planType" />
        <section class="create-account__section create-account__section--bb">
            <h2 class="subtitle">
                Account Information
                <a class="subtitle__right link" href="/microsoftidentity/account/signin">
                    Already have an account?
                </a>
            </h2>
            <div class="form-field">
                <label class="form-field__label" for="email">Email</label>
                <input class="form-field__input" title="Email Address" type="email" id="email" name="Email" v-model.lazy="email" pattern="[A-Za-z0-9._%+-]{2,}@[a-zA-Z0-9._%+-]{1,}([.]{1}[a-zA-Z0-9._%+-]{2,}|[.]{1}[a-zA-Z0-9._%+-]{2,}[.]{1}[a-zA-Z0-9._%+-]{2,})">
            </div>
            <div id="required-msg-1">
                <div v-cloak v-show="!isEmailEntered">
                    <span v-if="!isEmailEntered" class="form-field-error-required">Email is required</span>
                    <span v-else class="form-field-error-required">&nbsp;</span>
                </div>
            </div>
            <div class="form-field-error-msg" v-cloak v-show="!isFormRow1Valid">
                <span class="form-field__validation form-field__validation--error form-field__validation--error-icon" v-cloak v-show="emailLookupState === EmailLookupState.Exists">
                    A user with this email address already exists. Please use a different email address or Sign In to your existing account.
                </span>
            </div>
            <div class="form-field-container">
                <div class="form-field">
                    <label class="form-field__label" for="firstName">First Name</label>
                    <input class="form-field__input" type="text" id="firstName" name="FirstName" v-model="firstName">
                </div>
                <div class="form-field">
                    <label class="form-field__label" for="lastName">Last Name</label>
                    <input class="form-field__input" type="text" id="lastName" name="LastName" v-model="lastName">
                </div>
            </div>
            <div id="required-msg-2">
                <div v-cloak v-show="!isFirstNameEntered || !isLastNameEntered">
                    <span v-if="!isFirstNameEntered" class="form-field-error-required">First name is required</span>
                    <span v-else class="form-field-error-required">&nbsp;</span>
                    <span v-if="!isLastNameEntered" class="form-field-error-required-second">Last name is required</span>
                    <span v-else class="form-field-error-required-second">&nbsp;</span>
                </div>
            </div>
            <div class="form-field-container">
                <div class="form-field">
                    <label class="form-field__label" for="password">Password</label>
                    <input class="form-field__input" :type="passwordInputType" id="password" name="Password" v-model="password" autocomplete="new-password">
                </div>
                <div class="form-field">
                    <label class="form-field__label" for="industry">I Work In</label>
                    <select class="form-field__input" id="industry" name="industry" v-model="industry">
                        <option value="" disabled hidden>Select an industry</option>
                        <option v-for="industry in industries" :value="industry.id">{{ industry.name }}</option>
                    </select>
                </div>
            </div>
            <div id="required-msg-1">
                <div v-cloak v-show="!isPasswordEntered">
                    <span v-if="!isPasswordEntered" class="form-field-error-required">Password is required</span>
                    <span v-else class="form-field-error-required-second">&nbsp;</span>
                </div>
            </div>
            <div class="form-field-error-msg" v-cloak v-show="!isFormRow1Valid">
                <span class="form-field__validation form-field__validation--error form-field__validation--error-icon" v-cloak v-show="!isPasswordValid">
                    The password should be between 8 and 64 characters in length and use 3 of 4 character classes: uppercase, lowercase, number or symbol
                </span>
            </div>
            <div id="required-msg-3">
                <div v-cloak v-show="!isPasswordEntered || !isIndustryEntered">
                    <span v-if="!isPasswordEntered" class="form-field-error-required">Password is required</span>
                    <span v-else class="form-field-error-required">&nbsp;</span>
                    <span v-if="!isIndustryEntered" class="form-field-error-required-second">Industry is required</span>
                    <span v-else class="form-field-error-required-second">&nbsp;</span>
                </div>
            </div>
        </section>
        <section class="create-account__section">
            <h2 class="subtitle">
                Add Locations
            </h2>
            <div class="locations-container" v-for="(location, idx) in locations" :key="location.id">
                <div class="form-field">
                    <div class="form-field__label form-field__label--with-link">
                        <label v-if="idx == 0">Location {{ numbers[idx] }} (Required)</label>
                        <label v-else-if="idx >= maximumLocationsAllowed">Location {{ numbers[idx] }} (Add-on)</label>
                        <label v-else>Location {{ numbers[idx] }}</label>
                        <a class="link" href="#" v-show="idx >= maximumLocationsAllowed" v-on:click="removeLocation(idx, ...arguments)" >Remove</a>
                    </div>

                    <autocomplete
                        :id="'location' + idx.toString()"
                        :name="'location' + idx.toString()"
                        :ref="'autocomplete' + idx.toString()"
                        :source="getLocationResults"
                        results-value="key"
                        results-display="displayName"
                        input-class="form-field__input autocomplete"
                        placeholder="City or ZIP code"
                        :showNoResults="false"
                        v-on:selected="setLocation(idx, ...arguments)"
                        v-on:clear="clearLocation(idx)"
                        v-on:nothingSelected="onNothingSelected(idx, ...arguments)"
                        v-on:noResults="onNoResults(idx, ...arguments)">
                    </autocomplete>

                    <input type="hidden"
                            id="locationId"
                            :name="'locations[' + idx.toString() + '].key'"
                            v-model="location.key" />

                    <input type="hidden"
                            id="locationName"
                            :name="'locations[' + idx.toString() + '].name'"
                            v-model="location.name" />  

                    <span v-cloak v-show="location.state === locationState.valid" class="form-field__validation form-field__validation--success">
                        This is a valid location
                    </span>

                    <span v-cloak v-show="location.state === locationState.invalid" class="form-field__validation form-field__validation--error form-field__validation--error-icon" >
                        {{ location.error }}
                    </span>
                </div>
                <div class="form-field" v-show="isEventTypesVisible">
                    <span class="form-field__label">Weather events for this location</span>
                    <div class="form-field__checkboxes">
                        <label class="form-field__checkbox" v-for="eventType in location.eventTypes">
                            <input type="checkbox"
                                    :value="eventType.id"
                                    :name="'locations[' + idx.toString() + '].eventTypeIds'"
                                    v-model="eventType.isChecked">
                            {{ eventType.displayName }}
                        </label>
                    </div>
                </div>
            </div>
            <p v-show="isManageLocationsAddOnEnabled" >
                Add more locations for an additional {{ symbol }}{{addOnPrice}} per location.
            </p>
            <a class="link" v-show="canAddLocation && plan.id == 2 && isManageLocationsAddOnEnabled" v-on:click="addLocation">
                + Add a Location
            </a>
            <a class="link" v-show="!canAddLocation && plan.id == 2 && isManageLocationsAddOnEnabled">
                Fill all your locations to unlock this feature.
            </a>
            <a :href="changePlanUrl" class="link" v-show="plan.id == 1 && isManageLocationsAddOnEnabled">
                Upgrade your plan to unlock this feature.
            </a>
        </section>
    </div>
    <aside class="create-account-aside">
        <aside class="create-account-aside-order-summary">
            <h4 class="create-account-aside__title">Order Summary</h4>
            <div class="field-row-order-summary">
                <p>
                    Snow Alerts {{ plan.name }}
                </p>
                <p>
                    {{ plan.price }}
                </p>
            </div>
            <div class="field-row-order-summary" v-show="numberOfAddOns > 0">
                <p>
                    Locations Add-on ({{ numberOfAddOns }}x{{ symbol }}{{ addOnPrice }})
                </p>
                <p>
                    {{ symbol }}{{ totalAddOn }}
                </p>
            </div>
            <div class="field-row-order-summary-total">
                <p>
                    <strong>Total Due Now</strong>
                </p>
                <p v-if="numberOfAddOns > 0">
                    <strong>{{ symbol }}{{ totalOrderSummary }}</strong>
                </p>
                <p v-else>
                    <strong>{{ plan.price }}</strong>
                </p>
            </div>
            <p>
                You'll be charged annually until you cancel your subscription.
                Your subscription will auto-renew on {{ plan.expiration }}.
            </p>
            <a :href="changePlanUrl" class="create-account-aside__change-plan link">Change Plan &#129106;</a>
            <p class="create-account-aside__text">
                AccuWeather is committed to protecting and respecting your privacy.
                We will only use your personal information to administer your account
                and provide the products and services you requested from us. From time
                to time, we would like to contact you about our products and services,
                as well as other content that may be of interest to you.
            </p>
            <div class="create-account-aside__checkboxes">
                <label class="form-field__checkbox form-field__checkbox--agreement">
                    <input type="checkbox" v-model="isTermsOfUseAccepted">
                    <span style="margin-left: 8px;">
                        By signing up, you agree to our
                        <a href="https://www.accuweather.com/en/legal" rel="noopener noreferrer" target="_blank" class="account-disclaimer__link">
                            Terms of Use
                        </a>
                        and
                        <a href="https://www.accuweather.com/en/cookiepolicy" rel="noopener noreferrer" class="account-disclaimer__link" target="_blank">
                            Privacy Policy
                        </a>.
                    </span>
                </label>
                <label class="form-field__checkbox form-field__checkbox--agreement form-field__checkbox--atop">
                    <input type="checkbox" name="allowMarketingEmails" v-model="allowMarketingEmails" value="true">
                    <span style="margin-top: 2px;margin-left: 8px;">
                        By checking this box, you allow AccuWeather to store and process
                        the personal information submitted to provide additional content,
                        including marketing offers and updates.
                    </span>
                </label>
            </div>
            <div class="button-container">
                <button class="button button--mobile-w100" type="submit" :disabled="!isFormValid">
                    Continue to Payment
                </button>
            </div>
        </aside>
    </aside>
</form>
`
});